import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

const MAKER_CHECKER_ENTITIES = [
  { key: 'loan_approval', label: 'Loan Approval', description: 'Require checker approval for loan approvals' },
  { key: 'loan_disbursement', label: 'Loan Disbursement', description: 'Require checker approval for disbursements' },
  { key: 'loan_repayment', label: 'Loan Repayment Adjustments', description: 'Require checker for repayment adjustments' },
  { key: 'loan_write_off', label: 'Loan Write-Off', description: 'Require checker for loan write-offs' },
  { key: 'savings_approval', label: 'Savings Account Approval', description: 'Require checker for savings approvals' },
  { key: 'savings_withdrawal', label: 'Large Withdrawals', description: 'Require checker for withdrawals above threshold' },
  { key: 'journal_entries', label: 'Journal Entries', description: 'Require checker for manual journal entries' },
  { key: 'client_creation', label: 'Client Creation', description: 'Require checker for new client registration' },
  { key: 'user_creation', label: 'User Creation', description: 'Require checker for new system users' },
  { key: 'office_creation', label: 'Office Creation', description: 'Require checker for new offices' },
  { key: 'compliance_review', label: 'Sanctions Escalation Review', description: 'Manager review for sanctions/PEP escalations' },
];

const ROLES = [
  { value: 'any', label: 'Any (admin or manager)' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin only' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'accountant', label: 'Accountant' },
];

// Normalise legacy values: boolean => { enabled, required_role: 'any' }
const normalize = (raw) => {
  const out = {};
  for (const e of MAKER_CHECKER_ENTITIES) {
    const v = raw?.[e.key];
    if (v && typeof v === 'object') {
      out[e.key] = { enabled: !!v.enabled, required_role: v.required_role || 'any' };
    } else {
      out[e.key] = { enabled: !!v, required_role: 'any' };
    }
  }
  return out;
};

const MakerCheckerPage = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/maker-checker-config'); setConfig(normalize(res.data || {})); }
    catch { setConfig(normalize({})); }
    setLoading(false);
  };

  const toggle = (key) => setConfig(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key]?.enabled } }));
  const setRole = (key, role) => setConfig(prev => ({ ...prev, [key]: { ...prev[key], required_role: role } }));

  const handleSave = async () => {
    // Persist objects so backend can read required_role; backwards-compatible.
    try { await api.put('/maker-checker-config', config); toast.success('Configuration saved'); }
    catch { toast.error('Failed to save'); }
  };

  return (
    <AdminSubPage title="Configure Maker Checker Tasks" breadcrumb={['Admin', 'System', 'Maker Checker']} backPath="/dashboard/admin/system" actionLabel="Save" onAction={handleSave}>
      <div className="bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-xs text-blue-800">
        <strong>How it works:</strong> When enabled, the action goes into the <a href="/dashboard/approval-queue" className="underline">Approval Queue</a> and the maker cannot approve their own request. <em>Required Approver</em> restricts who can sign off — admins can always approve regardless of this setting.
      </div>
      {loading ? <div className="text-center py-8 text-gray-400">Loading...</div> : (
        <div className="bg-white border border-gray-200 divide-y" data-testid="mc-list">
          {MAKER_CHECKER_ENTITIES.map(e => {
            const row = config[e.key] || { enabled: false, required_role: 'any' };
            return (
              <div key={e.key} className="grid grid-cols-12 items-center gap-3 px-6 py-4">
                <div className="col-span-7">
                  <div className="text-sm font-medium text-[#1f1f1f]">{e.label}</div>
                  <div className="text-xs text-gray-400">{e.description}</div>
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-gray-500">Required approver</Label>
                  <Select value={row.required_role} onValueChange={(v) => setRole(e.key, v)} disabled={!row.enabled}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`mc-role-${e.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex justify-end">
                  <Switch checked={!!row.enabled} onCheckedChange={() => toggle(e.key)} data-testid={`mc-${e.key}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminSubPage>
  );
};
export default MakerCheckerPage;
